import moment from "moment";

import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { OffendingAgent } from "@/Common/Api/MedfarDictionaries/Dto/Responses/OffendingAgent";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { MedicalItemsTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { OffendingAgentSearchAutocomplete } from "@/Patient/Components/MedicalItems/Reactions/OffendingAgentSearchAutocomplete";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { LifeStageModeEnum } from "@/Patient/Models/LifeStageModeEnum";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { MedicalItem, MedicalItemModel } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemMedicalReaction } from "@/Patient/Models/MedicalSummary/MedicalItemMedicalReaction";
import { BaseMedicalItemsView, IItemDetailsRenderFunction, IListItemRenderFunction, IMedicalItemsViewProps } from "../BaseMedicalItemsView";
import { BaseMedicalItemsViewState } from "../BaseMedicalItemsViewState";
import { LifeStageRow } from "../LifeStageRow";

class OffendingAgentInfo {
    public Id: string;
    public OffendingAgentName: string;
    public ActiveIngredientName: string;
    public CodeGEN: string;
}

class MedicalItemsMedicalReactionViewState extends BaseMedicalItemsViewState {
    public IsNewEMRTemplate: boolean;

    public OffendingAgents: OffendingAgentInfo[];

    public AvailableAllergyType: FlatLookupView[];
    public SelectedAllergyTypeLookupId: string;

    public OffendingAgent: string;
    public ActiveIngredient: string;
    public CodeGEN: string;

    public Description: string;

    public AvailableSeverities: FlatLookupView[];
    public SelectedSeverityLookupId: string;

    public AvailableClinicalStatus: FlatLookupView[];
    public SelectedClinicalStatusLookupId: string;
    public EndDate: Date;
    public IsEndDateApproximative: boolean;

    public Notes: string;

    public Id: string;
    public DisplayId: string;

    constructor(s: MedicalItemsMedicalReactionViewState = null) {
        super(s);

        if (s != null) {
            this.IsNewEMRTemplate = s.IsNewEMRTemplate;
            this.OffendingAgents = s.OffendingAgents;
            this.AvailableAllergyType = s.AvailableAllergyType;
            this.SelectedAllergyTypeLookupId = s.SelectedAllergyTypeLookupId;
            this.OffendingAgent = s.OffendingAgent;
            this.ActiveIngredient = s.ActiveIngredient;
            this.CodeGEN = s.CodeGEN;
            this.Description = s.Description;
            this.AvailableSeverities = s.AvailableSeverities;
            this.SelectedSeverityLookupId = s.SelectedSeverityLookupId;
            this.AvailableClinicalStatus = s.AvailableClinicalStatus;
            this.SelectedClinicalStatusLookupId = s.SelectedClinicalStatusLookupId;
            this.LifeStageAvailableAgeAtOnsetUnits = s.LifeStageAvailableAgeAtOnsetUnits;
            this.Notes = s.Notes;
            this.Id = s.Id;
            this.EndDate = s.EndDate;
        }
        else {
            this.AvailableAllergyType = cacheStore.FindLookupsByTypeCode("REACTIONTYPE");
            this.SelectedAllergyTypeLookupId = cacheStore.FindLookupByLookupCode("Allergy ", "REACTIONTYPE").LookupID; // yes there is a space in lookupcode
            this.AvailableSeverities = cacheStore.FindLookupsByTypeCode("SEVERITY");
            this.SelectedSeverityLookupId = cacheStore.FindLookupByLookupCode("Unknown", "SEVERITY").LookupID;
            this.AvailableClinicalStatus = cacheStore.FindLookupsByTypeCode("CLINICALSTATUS");
            this.SelectedClinicalStatusLookupId = cacheStore.FindLookupByLookupCode("ClinicalUnconfirmed", "CLINICALSTATUS").LookupID;
            this.LifeStageAvailableAgeAtOnsetUnits = cacheStore.FindLookupsByTypeCode("DayYear");
            this.OffendingAgents = [];
            this.OffendingAgent = "";
            this.ActiveIngredient = "";
            this.Description = "";
            this.Notes = "";
        }
    }
}

class MedicalItemsMedicalReactionView extends BaseMedicalItemsView<IMedicalItemsViewProps, MedicalItemsMedicalReactionViewState> {
    public componentWillMount(): void {
        var s = null;
        let cacheId = this.getItemCacheId(this.props);
        if (cacheId != null) {
            s = patientStore.getMedicalItemCachedState(this.props.PatientContactId, cacheId);
        }
        if (s == null) {
            s = new MedicalItemsMedicalReactionViewState();
        }
        this.setState(s);
    }

    public componentWillReceiveProps(nextProps: IMedicalItemsViewProps): void {
        // when the model changes, check if we have to render an item in edit mode, if so, we set the state so we can edit its values
        const model = nextProps.Model;

        if (model != this.props.Model) {
            const isEditing = this.isEditing(model);
            const medicalItems = model.get("MedicalItemsList")?.toArray() ?? [];

            if (isEditing && medicalItems && medicalItems.length > 0) {
                const latestNewItem = MedicalItemModel.sortByCreationDateDesc(medicalItems.filter(x => isGuidNullOrUndefinedOrEmpty(x.Id))).firstOrDefault();

                if (latestNewItem != null) {
                    // Use the new MedicalItem
                    this.SetEditedItemState(latestNewItem);
                }
                else {
                    // modify the last item if Uncommitted
                    const latestMedicalItem = MedicalItemModel.sortByCreationDateDesc(medicalItems).firstOrDefault();

                    if (latestMedicalItem != null && !latestMedicalItem.IsCommitted)
                        this.SetEditedItemState(latestMedicalItem);
                }
            }
        }
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    public componentDidUpdate(): void {
        $(window).trigger("resize");
    }

    public componentWillUnmount(): void {
        patientStore.setMedicalItemCachedState(this.props.PatientContactId, this.state.Id, this.state);
    }

    public render(): JSX.Element {
        // get reaction type looks for filtering
        var allergyTypeLookup = cacheStore.FindLookupByLookupCode("Allergy ");
        var adverseReactionTypeLookup = cacheStore.FindLookupByLookupCode("Adverse Reaction ");
        var intoleranceTypeLookup = cacheStore.FindLookupByLookupCode("Intolerance");

        var self = this;
        var state = this.state;

        let listItemRenderFunction: IListItemRenderFunction = function(reaction: MedicalItemMedicalReaction): JSX.Element {
            var reactionLogo = (
                <div className="medical-reaction c1">
                    <ul className="circles">
                        <li>
                            <span className="circle-gray">
                                <i>R</i>
                            </span>
                        </li>
                    </ul>
                </div>
            );

            var intoloeranceLogo = (
                <div className="medical-reaction c1">
                    <ul className="circles">
                        <li>
                            <span className="circle-grayblue">
                                <i>I</i>
                            </span>
                        </li>
                    </ul>
                </div>
            );

            var allergyLogo = (
                <div className="medical-reaction c1">
                    <ul className="circles">
                        <li>
                            <span className="circle-red">
                                <i>A</i>
                            </span>
                        </li>
                    </ul>
                </div>
            );

            var logo: JSX.Element = null;

            if (reaction.ReactionTypeLookupId == allergyTypeLookup.LookupID)
                logo = allergyLogo;
            if (reaction.ReactionTypeLookupId == adverseReactionTypeLookup.LookupID)
                logo = reactionLogo;
            if (reaction.ReactionTypeLookupId == intoleranceTypeLookup.LookupID)
                logo = intoloeranceLogo;

            var lifeStageString = PatientMedicalSummaryHelper.GetLifeStageString(reaction);

            return (
                <div className="med-note">
                    <div className="flex-container medical-note">
                        <div className="flx-col a">
                            <p className="ellipsis"><strong>{reaction.OffendingAgent}</strong></p>
                            {reaction.EMRTemplateIsInvalid && <p className="ellipsis red-text"> {cacheStore.GetString("InvalidMLabel")}</p>}
                            <p className="ellipsis">{lifeStageString}</p>
                        </div>
                        <div className="flx-col b">
                            {logo}
                        </div>
                    </div>
                </div>
            );
        };

        let itemDetailsRenderFunction: IItemDetailsRenderFunction = function(reaction: MedicalItemMedicalReaction): JSX.Element {
            var lifeStageString = PatientMedicalSummaryHelper.GetLifeStageString(reaction);

            var endDateDisplay = reaction.EndDate != null ? moment(reaction.EndDate).format("YYYY-MM-DD") : "";
            return (
                <div className="medical-reaction-detail-box">
                    <table className="medical-reaction-details border">
                        <tbody>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("ReactionTypeDLabel")}</span>
                                </td>
                                <td className="c2" data-cy="medicalItems-medicalReaction-type">
                                    <strong>{MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(reaction.ReactionTypeLookupId))}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("OffendingAgentDLabel")}</span>
                                </td>
                                <td className="c2" data-cy="medicalItems-medicalReaction-agent">
                                    <strong>{MyleHelper.ReplaceEmptyString(reaction.OffendingAgent)}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("ReactionDescriptionDLabel")}</span>
                                </td>
                                <td className="c2" data-cy="medicalItems-medicalReaction-description">
                                    <strong>{JSXHelper.BreakStringIntoParagraphs(MyleHelper.ReplaceEmptyString(reaction.ReactionDescription))}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("StartDateLabel")}</span>
                                </td>
                                <td className="c2" data-cy="medicalItems-medicalReaction-lifeStage">
                                    <strong>{MyleHelper.ReplaceEmptyString(lifeStageString)}</strong>
                                </td>
                            </tr>
                            {reaction.EndDate != null && (
                                <tr>
                                    <td className="c1">
                                        <span>{cacheStore.GetString("EndDateDLabel")}</span>
                                    </td>
                                    <td className="c2" data-cy="medicalItems-medicalReaction-endDate">
                                        <strong>{endDateDisplay}</strong>
                                    </td>
                                </tr>
                            )}
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("SeverityDLabel")}</span>
                                </td>
                                <td className="c2" data-cy="medicalItems-medicalReaction-severity">
                                    <strong>{MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(reaction.SeverityLookupId))}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("ClinicalStatusDLabel")}</span>
                                </td>
                                <td className="c2" data-cy="medicalItems-medicalReaction-clinicalStatus">
                                    <strong>{MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(reaction.ClinicalStatusLookupId))}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("NotesLabel")}</span>
                                </td>
                                <td className="c2" data-cy="medicalItems-medicalReaction-notes">
                                    <strong>{JSXHelper.BreakStringIntoParagraphs(MyleHelper.ReplaceEmptyString(reaction.Notes))}</strong>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            );
        };

        let itemDetailsEditRenderFunction: IItemDetailsRenderFunction = function(reaction: MedicalItemMedicalReaction): JSX.Element {
            var reactionGroupsRender: JSX.Element[] = [];

            state.OffendingAgents.forEach(x => {
                reactionGroupsRender.push(
                    <div className="row" key={"offendinggent_" + x.Id}>
                        <div className="col-sm-12" style={{ "textAlign": "left" }}> <strong>{x.OffendingAgentName} </strong> {x.ActiveIngredientName != null && x.ActiveIngredientName.trim().length > 0 ? "(" + x.ActiveIngredientName + ")" : ""} <span onClick={self.RemoveOffendingAgentFromBatch.bind(self, x)} style={{ "paddingLeft": "10px", "color": "red", "fontWeight": "bold", "cursor": "pointer" }}>X</span></div>
                    </div>
                );
            });

            var isOffendingAgentValid = false;
            if (state.IsNewEMRTemplate) {
                isOffendingAgentValid = state.OffendingAgents != null &&
                    state.OffendingAgents.length > 0 &&
                    state.OffendingAgents.some(x => x.OffendingAgentName != null && x.OffendingAgentName.trim().length > 0);
            }
            else {
                isOffendingAgentValid = state.OffendingAgent != null && state.OffendingAgent.trim().length > 0;
            }

            return (
                <div className="medical-reaction-detail-box">
                    <table className="medical-problems-edit border">
                        <tbody>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("TypeLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <SelectLookupControl
                                        dataCy="medicalItems-medicalReaction-type"
                                        disabled={state.OffendingAgents != null && state.OffendingAgents.length >= 1}
                                        IncludeEmptyChoice={false}
                                        Value={state.SelectedAllergyTypeLookupId}
                                        AvailableValues={state.AvailableAllergyType.map(x => x.ToComboboxItem())}
                                        Callback={self.AllergyTypeChanged.bind(self)}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("OffendingAgentDLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <OffendingAgentSearchAutocomplete
                                        dataCy="medicalItems-medicalReaction-agent"
                                        valid={isOffendingAgentValid}
                                        debounce={400}
                                        onBlurCallback={self.OffendingAgentOnBlur.bind(self)}
                                        onItemSelectCallback={self.OffendingAgentTextSelected.bind(self)}
                                        newValue={state.OffendingAgent}
                                    />
                                </td>
                            </tr>
                            {!reaction.IsNewEMRTemplate && (
                                <tr>
                                    <td className="c1">
                                        <span>{cacheStore.GetString("ActiveIngredientsLabel")}  </span>
                                    </td>
                                    <td className="c2" colSpan={2}>
                                        {state.ActiveIngredient != null && state.ActiveIngredient.trim().length > 0 ? state.ActiveIngredient : "--"}
                                    </td>
                                </tr>
                            )}
                            {reaction.IsNewEMRTemplate && (
                                <tr>
                                    <td className="c1" />
                                    <td className="c2" colSpan={2}>

                                        {reactionGroupsRender}

                                    </td>
                                </tr>
                            )}

                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("ReactionDescriptionDLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <TextAreaResizable
                                        minHeightPx={100}
                                        id={"diag_desc" + (reaction.displayId ? reaction.displayId : reaction.MedicalItemId)}
                                        dataCy="medicalItems-medicalReaction-description"
                                        text={state.Description}
                                        isDisabled={false}
                                        onChange={value => self.ReactionDescriptionChanged(value)}
                                        setCursorStartOnFocus={false}
                                        resetTextOnIdChange={true}
                                    />
                                </td>
                            </tr>

                            <LifeStageRow
                                lifeStageMode={self.state.LifeStageMode}
                                lifeStageAvailableAgeAtOnsetUnits={self.state.LifeStageAvailableAgeAtOnsetUnits}
                                lifeStageAgeSelectedAgeAtOnsetUnit={self.state.LifeStageAgeSelectedgeAtOnsetUnit}
                                lifeStageSelectedLifeStageLookupId={self.state.LifeStageSelectedLifestageLookupId}
                                lifeStageAvailableLifeStages={self.state.LifeStageAvailableLifestages}
                                lifeStageAgeAtOnset={self.state.LifeStageAgeAtOnset}
                                lifeStageDatePrecision={self.state.LifeStageDatePrecision}
                                lifeStageDateTextboxValue={self.state.LifeStageDateTextboxValue}
                                lifeStageStartDate={self.state.LifeStageStartDate}
                                lifeStageIsStartDateApprox={self.state.LifeStageIsStartDateApproximative}
                                useThreeCols={false}
                                onLifeStageChange={value => self.LifeStageChanged(value)}
                                onLifeStageDateChange={(date, precision) => self.LifeStageDateChanged(date, precision)}
                                onLifeStageModeClick={newMode => self.LifeStageModeChanged(newMode)}
                                onLifeStageDateApproxChange={() => self.LifeStageDateApproxChange()}
                                onLifeStageDateTodayClick={() => self.LifeStageDateTodayClicked()}
                                onLifeStageAgeChange={value => self.LifeStageAgeChange(value)}
                                onLifeStageAgeUnitChange={value => self.LifeStageAgeUnitChange(value)}
                            />

                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("SeverityDLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <SelectLookupControl
                                        dataCy="medicalItems-medicalReaction-severity"
                                        IncludeEmptyChoice={false}
                                        Value={state.SelectedSeverityLookupId}
                                        AvailableValues={state.AvailableSeverities.map(x => x.ToComboboxItem())}
                                        Callback={self.SeverityChanged.bind(self)}
                                    />
                                </td>
                            </tr>

                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("ClinicalStatusDLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <SelectLookupControl
                                        dataCy="medicalItems-medicalReaction-clinicalStatus"
                                        IncludeEmptyChoice={false}
                                        Value={state.SelectedClinicalStatusLookupId}
                                        AvailableValues={state.AvailableClinicalStatus.map(x => x.ToComboboxItem())}
                                        Callback={self.ClinicalStatusChanged.bind(self)}
                                    />
                                </td>
                            </tr>

                            {state.SelectedClinicalStatusLookupId == cacheStore.FindLookupByLookupCode("Over", "CLINICALSTATUS").LookupID && (
                                <tr>
                                    <td className="c1">
                                        <span>{cacheStore.GetString("EndDateDLabel")}</span>
                                    </td>
                                    <td className="c2" colSpan={2}>
                                        <DateSelector
                                            constainerStyle={{ "display": "inline" }}
                                            id="enddate"
                                            dataCy="medicalItems-medicalReaction-endDate"
                                            disabled={false}
                                            date={state.EndDate}
                                            dateFormat="yyyy-MM-dd"
                                            onChangeCallback={endDate => self.EndDateChanged(endDate)}
                                        />
                                    </td>
                                </tr>
                            )}
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("NotesLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>

                                    <TextAreaResizable
                                        minHeightPx={100}
                                        id={"diag_notes" + (reaction.displayId ? reaction.displayId : reaction.MedicalItemId)}
                                        dataCy="medicalItems-medicalReaction-notes"
                                        text={state.Notes}
                                        isDisabled={false}
                                        onChange={value => self.NotesChanged(value)}
                                        setCursorStartOnFocus={false}
                                        resetTextOnIdChange={true}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            );
        };

        return (
            <BaseMedicalItemsView
                PatientContactId={this.props.PatientContactId}
                Model={this.props.Model}
                DetailsTitle={cacheStore.GetString("CppLabel") + " : " + cacheStore.GetString("AllergiesIntolerancesLabel")}
                ListItemRenderFunction={listItemRenderFunction}
                MedicalItemRenderFunction={itemDetailsRenderFunction}
                MedicalItemEditRenderFunction={itemDetailsEditRenderFunction}
                SaveFunction={self.Save.bind(self, false)}
                SavePlusFunction={self.SavePlus.bind(self)}
                emrType={MedicalItemTypeEnum.MedicalReaction}
                CheckIfSaveEnabledFunction={self.CheckIfSaveIsEnabled.bind(self)}
                toSearchableStringFunction={(items: MedicalItem[]) => self.searchMedicalItem(items)}
                showFavorites={this.props.showFavorites}
            />
        );
    }

    private SetEditedItemState(medItem: MedicalItem): void {
        let item = medItem as MedicalItemMedicalReaction;

        if (!item || item.IsCollapsed || item.IsCommitted)
            return;

        let isSameItem = item.displayId == this.state.DisplayId && item.Id == this.state.Id;
        if (isSameItem)
            return;

        var s = new MedicalItemsMedicalReactionViewState();
        s.Id = item.Id;
        s.DisplayId = item.Id;

        if (item.IsNewEMRTemplate) {
            // we have a new medical item / emr template : contol behaves in "multiple creation mode"...
            s.OffendingAgent = "";
            s.ActiveIngredient = "";
            s.CodeGEN = "";
            s.Notes = "";
        }
        else {
            // we are modifying an already saved medical item, control behaves normally
            s.Description = item.ReactionDescription;
            s.ActiveIngredient = item.ClassPharmacological;
            s.OffendingAgent = item.OffendingAgent;
            s.CodeGEN = item.CodeGEN;
            s.Notes = item.Notes;
        }

        s.IsNewEMRTemplate = item.IsNewEMRTemplate;

        s.LifeStageStartDate = item.LifeStageDate;
        s.LifeStageAgeAtOnset = item.LifeStageAge;
        s.EndDate = item.EndDate;

        s.LifeStageMode = item.LifeStageMode != null ? item.LifeStageMode : s.LifeStageMode;
        s.LifeStageSelectedLifestageLookupId = item.LifeStageStatusLookupId != null ? item.LifeStageStatusLookupId : s.LifeStageSelectedLifestageLookupId;
        s.LifeStageAgeSelectedgeAtOnsetUnit = item.LifeStageAgeStatusLookupId != null ? item.LifeStageAgeStatusLookupId : s.LifeStageAgeSelectedgeAtOnsetUnit;
        s.LifeStageDatePrecision = item.LifeStagePrecisionDate;
        s.LifeStageIsStartDateApproximative = !(item.LifeStagePrecisionDate == LifeStageDatePrecisionEnum.Day);
        s.LifeStageDateTextboxValue = s.LifeStageStartDate != null ? PatientMedicalItemsHelper.GetLifeStageDateString(s.LifeStageStartDate, item.LifeStagePrecisionDate) : "";

        s.SelectedClinicalStatusLookupId = item.ClinicalStatusLookupId != null ? item.ClinicalStatusLookupId : s.SelectedClinicalStatusLookupId;
        s.SelectedAllergyTypeLookupId = item.ReactionTypeLookupId != null ? item.ReactionTypeLookupId : s.SelectedAllergyTypeLookupId;
        s.SelectedSeverityLookupId = item.SeverityLookupId != null ? item.SeverityLookupId : s.SelectedSeverityLookupId;

        this.setState(s);
    }

    private AllergyTypeChanged(lookupId: string): void {
        var s = new MedicalItemsMedicalReactionViewState(this.state);
        s.SelectedAllergyTypeLookupId = lookupId;
        this.setState(s);
    }

    private OffendingAgentTextSelected(item: OffendingAgent): void {
        var s = new MedicalItemsMedicalReactionViewState(this.state);

        var isBatch = s.IsNewEMRTemplate;

        if (!(item == null) && !isBatch) {
            s.OffendingAgent = item.OffendingAgentName;
            s.ActiveIngredient = item.ActiveIngredientName;
            s.CodeGEN = item.CodeGen;
        }

        if (!(item == null) && isBatch) {
            var newOffendingAgent = new OffendingAgentInfo();
            newOffendingAgent.Id = GuidHelper.NewGuid();
            newOffendingAgent.ActiveIngredientName = item.ActiveIngredientName;
            newOffendingAgent.OffendingAgentName = item.OffendingAgentName;
            newOffendingAgent.CodeGEN = item.CodeGen;
            s.OffendingAgents.push(newOffendingAgent);

            s.OffendingAgent = "";
            s.ActiveIngredient = "";
        }

        this.setState(s);
    }

    private OffendingAgentOnBlur(onBlurText: string): void {
        if (onBlurText == null || onBlurText.trim().length == 0)
            return;

        var s = new MedicalItemsMedicalReactionViewState(this.state);

        if (s.IsNewEMRTemplate) {
            var newOffendingAgent = new OffendingAgentInfo();
            newOffendingAgent.Id = GuidHelper.NewGuid();
            newOffendingAgent.ActiveIngredientName = onBlurText;
            newOffendingAgent.OffendingAgentName = onBlurText;
            s.OffendingAgents.push(newOffendingAgent);
        }
        else {
            s.OffendingAgent = onBlurText;
            s.ActiveIngredient = s.ActiveIngredient ? s.ActiveIngredient : "--";
        }

        this.setState(s);
    }

    private ReactionDescriptionChanged(value: string): void {
        var s = new MedicalItemsMedicalReactionViewState(this.state);
        s.Description = value;
        this.setState(s);
    }

    private SeverityChanged(lookupId: string): void {
        var s = new MedicalItemsMedicalReactionViewState(this.state);
        s.SelectedSeverityLookupId = lookupId;
        this.setState(s);
    }

    private ClinicalStatusChanged(lookupId: string): void {
        var s = new MedicalItemsMedicalReactionViewState(this.state);
        s.SelectedClinicalStatusLookupId = lookupId;
        this.setState(s);
    }

    private EndDateChanged(date: Date): void {
        var s = new MedicalItemsMedicalReactionViewState(this.state);
        s.EndDate = moment(date).toDate();
        this.setState(s);
    }

    private NotesChanged(value: string): void {
        var s = new MedicalItemsMedicalReactionViewState(this.state);
        s.Notes = value;
        this.setState(s);
    }

    private LifeStageModeChanged(newMode: LifeStageModeEnum): void {
        var s = new MedicalItemsMedicalReactionViewState(this.state);
        s.LifeStageMode = newMode;

        if (s.LifeStageMode == LifeStageModeEnum.Age) {
            // s.LifeStageDatePrecision = LifeStageDatePrecisionEnum.MonthYear;
            s.LifeStageAgeSelectedgeAtOnsetUnit = cacheStore.FindLookupByLookupCode("year", "DayYear").LookupID;
            s.LifeStageAgeAtOnset = undefined;
            s.LifeStageIsStartDateApproximative = false;
        }
        else if (s.LifeStageMode == LifeStageModeEnum.Date) {
            s.LifeStageIsStartDateApproximative = s.LifeStageDatePrecision != LifeStageDatePrecisionEnum.Day;
        }

        this.setState(s);
    }

    private LifeStageDateApproxChange(): void {
        if (this.state && this.state.LifeStageDatePrecision == LifeStageDatePrecisionEnum.MonthYear || this.state.LifeStageDatePrecision == LifeStageDatePrecisionEnum.Year)
            return;

        var s = new MedicalItemsMedicalReactionViewState(this.state);
        s.LifeStageIsStartDateApproximative = !s.LifeStageIsStartDateApproximative;
        s.LifeStageDatePrecision = s.LifeStageIsStartDateApproximative ? LifeStageDatePrecisionEnum.DayApprox : LifeStageDatePrecisionEnum.Day;

        this.setState(s);
    }

    private LifeStageDateChanged(date: DateTimeWithPrecision, precision: LifeStageDatePrecisionEnum = LifeStageDatePrecisionEnum.Day): void {
        const s = new MedicalItemsMedicalReactionViewState(this.state);

        if (date.value) {
            s.LifeStageStartDate = moment(date.value).toDate();

            if (precision == LifeStageDatePrecisionEnum.DayApprox || precision == LifeStageDatePrecisionEnum.Day) {
                s.LifeStageDatePrecision = (this.state.LifeStageIsStartDateApproximative == true) ?
                    LifeStageDatePrecisionEnum.DayApprox :
                    LifeStageDatePrecisionEnum.Day;

                s.LifeStageDateTextboxValue = moment(date.value).format("YYYY-MM-DD"); // always iso 900somthing format
            }
            else {
                s.LifeStageDatePrecision = precision;
                s.LifeStageDateTextboxValue = PatientMedicalItemsHelper.GetLifeStageDateString(date.value, precision);
                s.LifeStageIsStartDateApproximative = true;
            }

            s.LifeStageMode = LifeStageModeEnum.Date;
        }
        else if (IsNullOrWhiteSpace(date.text)) {
            s.LifeStageStartDate = null;
            s.LifeStageDateTextboxValue = "";
            s.LifeStageIsStartDateApproximative = false;
        }

        this.setState(s);
    }

    private LifeStageDateTodayClicked(): void {
        var s = new MedicalItemsMedicalReactionViewState(this.state);
        s.LifeStageDateTextboxValue = moment(Date.now()).format("YYYY-MM-DD");
        s.LifeStageStartDate = moment(Date.now()).toDate();
        s.LifeStageMode = LifeStageModeEnum.Date;
        s.LifeStageDatePrecision = (this.state.LifeStageIsStartDateApproximative == true) ?
            LifeStageDatePrecisionEnum.DayApprox :
            LifeStageDatePrecisionEnum.Day;
        this.setState(s);
    }

    private LifeStageChanged(lookupId: string): void {
        var s = new MedicalItemsMedicalReactionViewState(this.state);
        s.LifeStageSelectedLifestageLookupId = lookupId;
        s.LifeStageMode = LifeStageModeEnum.LifeStage;
        this.setState(s);
    }

    private LifeStageAgeChange(value: string): void {
        var s = new MedicalItemsMedicalReactionViewState(this.state);
        s.LifeStageAgeAtOnset = PatientMedicalItemsHelper.parseLifeStageAgeAtOnsetValue(value);
        s.LifeStageMode = LifeStageModeEnum.Age;

        this.setState(s);
    }

    private LifeStageAgeUnitChange(lookupId: string): void {
        var s = new MedicalItemsMedicalReactionViewState(this.state);
        s.LifeStageAgeSelectedgeAtOnsetUnit = lookupId;
        s.LifeStageMode = LifeStageModeEnum.Age;
        this.setState(s);
    }

    private RemoveOffendingAgentFromBatch(offendingAgent: OffendingAgentInfo): void {
        if (!(offendingAgent == null)) {
            var s = new MedicalItemsMedicalReactionViewState(this.state);
            s.OffendingAgents = s.OffendingAgents.filter(x => x.Id !== offendingAgent.Id);
            this.setState(s);
        }
    }

    private async Save(isSavePlus: boolean = false): Promise<void> {
        let medicalItems = this.props.Model.get("MedicalItemsList").toArray();
        let editMedicalItem = medicalItems.firstOrDefault(x => x.IsCommitted == false) as MedicalItemMedicalReaction;
        editMedicalItem = MyleHelper.CloneObject(editMedicalItem);

        let medicalItemsToSend: MedicalItemMedicalReaction[] = [];

        if (editMedicalItem != null) {
            editMedicalItem.LifeStageAge = this.state.LifeStageAgeAtOnset;
            editMedicalItem.LifeStageAgeStatusLookupId = this.state.LifeStageAgeSelectedgeAtOnsetUnit;
            editMedicalItem.LifeStageDate = cacheStore.Helper.CreateDateAsUTC(this.state.LifeStageStartDate);
            editMedicalItem.LifeStageMode = this.state.LifeStageMode;
            editMedicalItem.LifeStagePrecisionDate = this.state.LifeStageDatePrecision;
            editMedicalItem.LifeStageStatusLookupId = this.state.LifeStageSelectedLifestageLookupId;

            if (!editMedicalItem.IsNewEMRTemplate) {
                editMedicalItem.OffendingAgent = this.state.OffendingAgent;
                editMedicalItem.ActiveIngredient = this.state.ActiveIngredient;
                editMedicalItem.ClassPharmacological = this.state.ActiveIngredient;
                editMedicalItem.CodeGEN = this.state.CodeGEN;
                editMedicalItem.IsDeleted = false;
                editMedicalItem.EndDate = cacheStore.Helper.CreateDateAsUTC(this.state.EndDate);
                editMedicalItem.SeverityLookupId = this.state.SelectedSeverityLookupId;
                editMedicalItem.ReactionTypeLookupId = this.state.SelectedAllergyTypeLookupId;
                editMedicalItem.ClinicalStatusLookupId = this.state.SelectedClinicalStatusLookupId;
                editMedicalItem.TypeLookupId = this.state.SelectedAllergyTypeLookupId;

                editMedicalItem.ReactionDescription = this.state.Description;
                editMedicalItem.Notes = this.state.Notes;
                medicalItemsToSend.push(editMedicalItem);
            }
            else if (this.state.OffendingAgents.length > 0) {
                for (let agent of this.state.OffendingAgents) {
                    if (agent != null && agent.OffendingAgentName != null && agent.OffendingAgentName.trim().length > 0) {
                        var batchMedicalItem: MedicalItemMedicalReaction = JSON.parse(JSON.stringify(editMedicalItem));

                        batchMedicalItem.EMRTemplateId = GuidHelper.NewGuid();
                        batchMedicalItem.MedicalItemId = GuidHelper.NewGuid();
                        batchMedicalItem.StartDateLifeStageId = GuidHelper.NewGuid();

                        batchMedicalItem.OffendingAgent = agent.OffendingAgentName;
                        batchMedicalItem.ActiveIngredient = agent.ActiveIngredientName;
                        batchMedicalItem.ClassPharmacological = agent.ActiveIngredientName;
                        batchMedicalItem.CodeGEN = agent.CodeGEN;
                        batchMedicalItem.IsDeleted = false;
                        batchMedicalItem.EndDate = cacheStore.Helper.CreateDateAsUTC(this.state.EndDate);
                        batchMedicalItem.SeverityLookupId = this.state.SelectedSeverityLookupId;
                        batchMedicalItem.ReactionTypeLookupId = this.state.SelectedAllergyTypeLookupId;
                        batchMedicalItem.ClinicalStatusLookupId = this.state.SelectedClinicalStatusLookupId;
                        batchMedicalItem.TypeLookupId = this.state.SelectedAllergyTypeLookupId;
                        batchMedicalItem.ReactionDescription = this.state.Description;
                        batchMedicalItem.Notes = this.state.Notes;

                        medicalItemsToSend.push(batchMedicalItem);
                    }
                }
            }
        }

        if (patientStore.IsMedicalItemFromOutsideNote) {
            await PatientMedicalItemsHelper.setOutsideNoteEncounter(medicalItemsToSend);
        }

        var operation = new LocalEvent(LocalPatientOperations.MedicalItems.Save);
        operation.AddItem("view", MedicalItemsTab.MedicalReactions);
        operation.AddItem("editedItems", medicalItemsToSend);
        if (isSavePlus)
            operation.AddItem("isSavePlus", true);

        LocalEventDispatcher.dispatch(operation);

        // clear our state
        var s = new MedicalItemsMedicalReactionViewState();
        s.Description = "";
        s.Notes = "";
        this.setState(s);
    }

    private async SavePlus(): Promise<void> {
        await this.Save(true);

        window.setTimeout(() => {
            $("#__offendingAgentSearchField").focus();
        }, 200);
    }

    private CheckIfSaveIsEnabled(): boolean {
        var isSaveEnabled = false;

        if (this.state.IsNewEMRTemplate) {
            isSaveEnabled = this.state.OffendingAgents != null &&
                this.state.OffendingAgents.length > 0 &&
                this.state.OffendingAgents.some(x => x.OffendingAgentName != null && x.OffendingAgentName.trim().length > 0);
        }
        else {
            isSaveEnabled = this.state.OffendingAgent != null && this.state.OffendingAgent.trim().length > 0;
        }

        return isSaveEnabled;
    }

    private searchMedicalItem(items: MedicalItem[]): MedicalItemMedicalReaction[] {
        let self = this;
        let medicalReactionMedicalItem: MedicalItemMedicalReaction[] = items as MedicalItemMedicalReaction[]; // cast MedicalItem to medicalReaction

        return medicalReactionMedicalItem.filterSearch(self.props.Model.get("SearchTerm"), item => {
            // items cannot be null for search, replace them with empty string
            let offendingAgent = MyleHelper.replaceNullWithEmptyString(item.OffendingAgent);
            let lifeStageAge = MyleHelper.replaceNullWithEmptyString(item.LifeStageAge);
            let lifeStageDate = MyleHelper.replaceNullWithEmptyString(item.LifeStageDate);
            let lifeStageStatusLookup = (cacheStore.GetLookupById(item.LifeStageStatusLookupId) != null) ? cacheStore.GetLookupById(item.LifeStageStatusLookupId).GetValue() : "";
            let typeLookup = (cacheStore.GetLookupById(item.TypeLookupId) != null) ? cacheStore.GetLookupById(item.TypeLookupId).GetValue() : "";

            return offendingAgent + " " + lifeStageAge + " " + lifeStageDate + " " + lifeStageStatusLookup + " " + typeLookup;
        });
    }
}

export { MedicalItemsMedicalReactionView };
